import { Fragment, useContext, useState, MouseEvent } from 'react';
import { MetadataError } from '@ep/error-handling';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Logout from '@mui/icons-material/Logout';
import { signOut } from 'firebase/auth';
import { Link } from 'react-router-dom';
import CardHeader from '@mui/material/CardHeader';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { red } from '@mui/material/colors';
import { useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import { auth } from '../../lib/google/firebase';
import { ReactComponent as UserManagementIcon } from '../../assets/icons/User Management.svg';
import { ReactComponent as SupportIcon } from '../../assets/icons/Response.svg';
import { UserContext } from '../../context/UserContext';
import { FeedbackSnackbarContext } from '../../context/FeedbackSnackbarContext';
import { ReactComponent as GroupIcon } from '../../assets/icons/Group Managment.svg';
import { getAdminGroups } from '../../util/user_access';
import { ReactComponent as MasterViewIcon } from '../../assets/icons/Master View.svg';
import { ReactComponent as ResetPasswordIcon } from '../../assets/icons/Reset Password.svg';
import { log } from '../../util/log';
import { generateUuidV4 } from '../../util/uuid';
import PasswordResetModalApp from './authentication/PasswordResetModalApp';

/**
 * A menu to find views not related to the specific company portal
 * @param {void} resetAppContext Reset all of the React context values used by the app to null
 * @returns {JSX.Element}
 */
const AccountMenu = ({ resetAppContext }: { resetAppContext: () => void }) => {
  const { user } = useContext(UserContext);
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [passwordResetModalOpen, setPasswordResetModalOpen] = useState<boolean>(false);
  const open = Boolean(anchorEl);

  const theme = useTheme();

  const admin_groups: Array<string> = user ? getAdminGroups(user) : [];
  const user_title = user?.super_admin
    ? 'App Administrator'
    : admin_groups.length > 0
      ? 'Group Administrator'
      : user?.type === 'external'
        ? 'External Contributor'
        : 'Company Contributor';

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handlePasswordResetClick = (): void => {
    setPasswordResetModalOpen(true);
    handleClose();
  };

  const handleSignOut = (): void => {
    try {
      signOut(auth)
        .then(() => {
          resetAppContext();
          theme.palette.primary.main = '#053d5d';
          theme.palette.secondary.main = '#000000';
          // Sign-out successful.
          location.replace('/');
        })
        .catch((err: unknown) => {
          const tracking_id: string = generateUuidV4();
          log(
            'error',
            new MetadataError(
              err instanceof Error
                ? err.message
                : 'Error: AccountMenu failed on an unknown error while calling handleSignOut.',
              { user: user },
              tracking_id
            )
          );
          setFeedbackData({
            message: `Unable to sign out at the moment. Tracking ID: ${tracking_id}`,
            state: true,
            type: 'error'
          });
        });
      handleClose();
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: AccountMenu failed on an unknown error while calling handleSignOut.',
          { user: user },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to sign out at the moment. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };

  return (
    <Fragment>
      <div data-testid="accountmenu_id" />
      <Box sx={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
        <Tooltip title="Account settings">
          <IconButton
            id="account-settings-hamburger"
            onClick={handleClick}
            size="small"
            sx={{ ml: 2 }}
            aria-controls={open ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
          >
            <MoreVertIcon sx={{ color: theme.palette.primary.main }} fontSize="medium" />
          </IconButton>
        </Tooltip>
      </Box>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0
            }
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {user && (
          <CardHeader
            avatar={
              <Avatar sx={{ bgcolor: red[500] }} aria-label="recipe">
                {user.first_name[0].toUpperCase()}
              </Avatar>
            }
            action={<></>}
            title={user.email}
            subheader={user_title}
          />
        )}
        {user?.super_admin && (
          <Link
            to="/settings/group-management"
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            <MenuItem onClick={handleClose} sx={{ p: 1.5 }}>
              <ListItemIcon sx={{ mr: 1 }}>
                <GroupIcon height={35} strokeWidth={3} />
              </ListItemIcon>
              Group Management
            </MenuItem>
          </Link>
        )}
        {(user?.super_admin || admin_groups.length > 0) && (
          <Link to="/settings/user-management" style={{ color: 'inherit', textDecoration: 'none' }}>
            <MenuItem onClick={handleClose} sx={{ p: 1.5 }}>
              <ListItemIcon sx={{ mr: 1 }}>
                <UserManagementIcon height={35} strokeWidth={2} />
              </ListItemIcon>
              User Management
            </MenuItem>
          </Link>
        )}
        {user?.super_admin && (
          <Link
            to="/settings/master/dashboard"
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            <MenuItem onClick={handleClose} sx={{ p: 1.5 }}>
              <ListItemIcon sx={{ mr: 1 }}>
                <MasterViewIcon height={35} />
              </ListItemIcon>
              Master View
            </MenuItem>
          </Link>
        )}
        <Link to="/contact" style={{ color: 'inherit', textDecoration: 'none' }}>
          <MenuItem onClick={handleClose} sx={{ p: 1.5 }}>
            <ListItemIcon sx={{ mr: 1 }}>
              <SupportIcon height={35} />
            </ListItemIcon>
            Log Support Request
          </MenuItem>
        </Link>
        <MenuItem onClick={handlePasswordResetClick} sx={{ p: 1.5 }}>
          <ListItemIcon sx={{ mr: 1 }}>
            <ResetPasswordIcon height={35} />
          </ListItemIcon>
          Reset Password
        </MenuItem>
        <MenuItem onClick={handleSignOut} sx={{ p: 3 }}>
          <Typography variant="subtitle1">Sign Out</Typography>
          <ListItemIcon sx={{ ml: 1 }}>
            <Logout fontSize="small" />
          </ListItemIcon>
        </MenuItem>
      </Menu>

      <PasswordResetModalApp
        show_modal={passwordResetModalOpen}
        onClose={() => setPasswordResetModalOpen(false)}
      />
    </Fragment>
  );
};

export default AccountMenu;
